import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Card, CardActionArea, CardActions, CardContent, CardMedia } from '@material-ui/core';
import { getStorageRef, getDocumentRef, getCollection, insertAt, getDotPosition } from 'utils';
import { Loader, CustomButton, AlertDialog, ViewDetails } from 'components';
import { Collections, isMobile } from 'app-constants';
import { withSnackbar } from 'notistack';
import BookForm from './BookForm/BookForm';
import Availability from './Availability/Availability';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => createStyles({
    root: {
      maxWidth: 500,
      minWidth: (isListView) => (!isListView && isMobile ? '' : 345),
      backgroundColor: theme.palette.action.disabledBackground
    },
    media: {
      height: 250,
      minHeight: (isListView) => (!isListView && isMobile ? 170 : 250)
    },
    cardContent: { padding: 8 }
  }));

function MediaCard({ data, handleOpenCarousel, index, enqueueSnackbar }) {
  const isListView = useSelector((state) => state.userPrefference.isListView);
  const classes = useStyles(isListView);
  const { t } = useTranslation();

  const { available, name, price, format, id, booked, tehnique, image, discount } = data;
  const { src, filename, resized } = image;

  const [resizedUrl, setResizedUrl] = useState('');
  const [isOpened, setIsOpened] = useState(false);
  const [isLoading, setIsloading] = useState(false);

  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const handleChangeName = (e) => setUserName(e.target.value);
  const handleChangePhone = (e) => setUserPhone(e.target.value);
  const handleChangeEmail = (e) => setUserEmail(e.target.value);

  const resetUserDetails = () => {
    setUserName('');
    setUserPhone('');
    setUserEmail('');
    setIsOpened(false);
  };

  const updateImageSrc = useCallback(() => {
    if (resizedUrl && !resized && !src.includes('_800x800')) {
      const ref = getDocumentRef(id, Collections.GALLERY);
      ref.update({
        'image.src': resizedUrl,
        'image.resized': true
      });
    }
  }, [id, src, resizedUrl, resized]);

  const displayMessage = useCallback(
    (message, variant) => enqueueSnackbar(message, { variant, preventDuplicate: true }),
    [enqueueSnackbar]
  );

  const getResizedImageUrl = useCallback(() => {
    const resizedImage = insertAt(filename, '_800x800', getDotPosition(filename));
    const ref = getStorageRef(`gallery/${resizedImage}`);
    ref
      .getDownloadURL()
      .then((url) => {
        setResizedUrl(url);
      })
      .catch((error) => {
        displayMessage(error, 'error');
      });
  }, [filename, displayMessage]);

  useEffect(() => {
    if (!resizedUrl && !src.includes('_800x800')) {
      getResizedImageUrl();
    } else {
      updateImageSrc();
    }
  }, [resizedUrl, src, getResizedImageUrl, updateImageSrc, displayMessage]);

  const submitDetails = () => {
    setIsloading(true);
    const newDate = new Date();
    const formattedDate = newDate.toLocaleDateString('ro-RO');
    const formattedTime = newDate.toLocaleTimeString('ro-RO');
    setTimeout(() => {
      if (!booked) {
        const orders = getCollection(Collections.ORDERS);
        orders
          .add({
            userName,
            userPhone,
            userEmail,
            created: `${formattedDate} ${formattedTime}`,
            products: [{ available, name, price, format, id, src }]
          })
          .then(() => {
            const ref = getDocumentRef(id, Collections.GALLERY);
            ref
              .update({
                booked: true
              })
              .then(() => displayMessage('Rezervat cu Succes', 'success'));
            resetUserDetails();
            setIsloading(false);
          })
          .catch((err) => {
            setIsloading(false);
            displayMessage(err.message, 'error');
          });
      } else {
        displayMessage('A apărut o eroare!', 'error');
      }
    }, 1000);
  };

  const isValidData = userName && userName.length > 0 && userPhone && userPhone.length === 9;

  return (
    <Card className={classes.root}>
      <CardActionArea>
        {resized ? (
          <>
            <CardMedia
              onClick={() => handleOpenCarousel(index)}
              className={classes.media}
              image={src}
              title={name}
            />
            <Availability available={available} booked={booked} />
          </>
        ) : (
          <Loader isLoading={true} />
        )}
        <CardContent className={classes.cardContent}>
          <ViewDetails discount={discount} tehnique={tehnique} price={price} format={format} name={name} />
        </CardContent>
      </CardActionArea>
      <CardActions style={{ alignItems: 'center', justifyContent: 'center' }}>
        <CustomButton
          title={t('order-now')}
          handleClick={() => setIsOpened(true)}
          isDisabled={!available || booked}
        />
      </CardActions>
      <AlertDialog
        alertTitle={t('order-details')}
        open={isOpened}
        yesAction={submitDetails}
        yesDisabled={!isValidData}
        yesButtonText={t('book')}
        cancelButtonText={t('cancel')}
        cancelAction={resetUserDetails}
        variant="outlined">
        <div style={{ alignItems: 'center', justifyContent: 'center' }}>
          <CardMedia className={classes.media} image={src} title={name} />
          <CardContent className={classes.cardContent}>
            <Loader isLoading={isLoading} />
            <ViewDetails discount={discount} tehnique={tehnique} price={price} format={format} name={name} />
            <BookForm
              name={userName}
              email={userEmail}
              phone={userPhone}
              handleChangeName={handleChangeName}
              handleChangePhone={handleChangePhone}
              handleChangeEmail={handleChangeEmail}
            />
          </CardContent>
        </div>
      </AlertDialog>
    </Card>
  );
}

export default withSnackbar(MediaCard);
